import { useState } from "react";

import {
  Avatar,
  Icon,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import { LojaDataGrid } from "layouts/pages/loja/common";

import { formatDateEndOfDay } from "helpers/formatDate";
import { phoneApplyMask } from "helpers/utils";
import { IAtleta } from "interfaces/atleta";
import { ATLETAS_ACCENT } from "../atletas.theme";

import FormAtleta from "./FormAtleta";

interface Props {
  data: IAtleta[];
  isLoading: boolean;
  hasUpdated: () => void;
}

export default function TableAtleta({ data, isLoading, hasUpdated }: Props) {
  const [modalViewAtleta, setModalViewAtleta] = useState(false);
  const [atletaSelected, setAtletaSelected] = useState<IAtleta | null>(null);

  // Definições das colunas da tabela dos atletas
  const columns: GridColDef<IAtleta>[] = [
    {
      field: "user",
      headerName: "",
      width: 50,
      disableColumnMenu: true,
      renderCell(params) {
        return (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Avatar alt={params.row.nome} src={params.row.user.avatar} />
          </div>
        );
      },
    },
    { field: "nome", headerName: "Nome", width: 300, disableColumnMenu: true },
    { field: "cpf", headerName: "CPF", width: 130, disableColumnMenu: true },
    {
      field: "dataNascimento",
      headerName: "Nascimento",
      width: 130,
      disableColumnMenu: true,
      renderCell(params) {
        return formatDateEndOfDay(params.row.dataNascimento, "dd/MM/yyyy");
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 300,
      disableColumnMenu: true,
      renderCell(params) {
        return (
          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip
              title={
                params.row.user.emailVerified
                  ? "Email verificado"
                  : "Email não verificado"
              }
            >
              <Icon
                fontSize="medium"
                color={params.row.user.emailVerified ? "success" : "error"}
              >
                {params.row.user.emailVerified ? "verified" : "gpp_maybe"}
              </Icon>
            </Tooltip>
            <Typography variant="body2">{params.row.email}</Typography>
          </MDBox>
        );
      },
    },
    {
      field: "telefone",
      headerName: "Telefone",
      width: 130,
      disableColumnMenu: true,
      renderCell(params) {
        return phoneApplyMask(params.row.celPhone) || "-";
      },
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 100,
      disableColumnMenu: true,
      align: "center",
      renderCell(params) {
        return (
          <MDBox display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleAtletaSelected(params.row);
              }}
            >
              <Icon fontSize="small" sx={{ color: ATLETAS_ACCENT }}>
                edit_note
              </Icon>
            </IconButton>
            {params.row.user.passwordResetToken && (
              <Tooltip title={`código: ${params.row.user.passwordResetToken}`}>
                <Icon color="warning">key</Icon>
              </Tooltip>
            )}
          </MDBox>
        );
      },
    },
  ];

  // Selecionar atleta
  const handleAtletaSelected = (atleta: IAtleta) => {
    setAtletaSelected(atleta);
    setModalViewAtleta(true);
  };

  return (
    <>
      <LojaDataGrid
        loading={isLoading}
        columns={columns}
        rows={data}
        hideFooter
        getRowId={(row) => row.id}
        onRowClick={(params) => handleAtletaSelected(params.row)}
        localeText={{
          noRowsLabel: "Nenhum atleta cadastrado",
        }}
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 2,
          "& .MuiDataGrid-row": { cursor: "pointer" },
          "& .MuiDataGrid-row:hover": {
            bgcolor: "rgba(249, 96, 48, 0.04)",
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "grey.100",
            fontWeight: 700,
          },
          "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
            outline: "none",
          },
        }}
      />

      {atletaSelected && (
        <FormAtleta
          atleta={atletaSelected}
          open={modalViewAtleta}
          handleClose={setModalViewAtleta}
          hasUpdated={hasUpdated}
        />
      )}
    </>
  );
}
